import React, { useMemo, useRef, useState } from 'react';
import { Group, Stack } from '@mantine/core'; 
import {
  Chart as ChartJS,
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import dayjs from 'dayjs';
import accounting from 'accounting';
import EmptyStateDashboard from '../../EmptyStateContainer/EmptyStateDashboard';
import RentIcon from '../../../../../../assets/images/module/general/IncomeExpenditureIcon.svg';
import moment from 'moment';
import { getAllTaxCostTypes } from '../../../../../helpers/Utils';

ChartJS.register(
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController
);

const colors = {
  income: '#8B88F5',
  expenditure: '#B8B8F4',
  balance: '#5555FF',
}

// Custom Skeleton Loader mapping your exact Chart configuration
const CustomChartLoader = () => {
  const bars = Array.from({ length: 12 });
  return (
    <div style={{ position: 'relative', width: '100%', height: '180px', overflow: 'hidden' }}>
      <style>
        {`
          @keyframes barBounceTop {
            0%, 100% { height: 5%; }
            50% { height: 40%; }
          }
          @keyframes barBounceBottom {
            0%, 100% { height: 5%; }
            50% { height: 35%; }
          }
          @keyframes waveSlide {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}
      </style>

      {/* Background Grid Lines (evenly spaced background horizontal lines) */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', zIndex: 0 }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ borderTop: '1px solid #EBEBEB', width: '100%' }} />
        ))}
      </div>

      {/* Zero Axis Line (middle) */}
      <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px solid #DADAE6', zIndex: 1 }} />

      {/* Animated Bars (Income rising up, Expenditure falling down - Side by Side) */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'space-between', zIndex: 2, paddingLeft: '20px', paddingRight: '20px' }}>
        {bars.map((_, i) => (
          <div key={i} style={{ display: 'flex', width: '45px', height: '100%', gap: '1px', justifyContent: 'center' }}>
            
            {/* Einnahmen (Income Bar - Left side, pointing up) */}
            <div style={{ position: 'relative', flex: 1, height: '100%' }}>
              <div style={{
                position: 'absolute',
                bottom: '50%',
                left: 0,
                width: '100%',
                backgroundColor: colors.income,
                borderRadius: '3px 3px 0 0',
                opacity: 0.6,
                animation: 'barBounceTop 2s infinite ease-in-out',
                animationDelay: `${i * 0.15}s`
              }} />
            </div>
            
            {/* Ausgaben (Expenditure Bar - Right side, pointing down) */}
            <div style={{ position: 'relative', flex: 1, height: '100%' }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                width: '100%',
                backgroundColor: colors.expenditure,
                borderRadius: '0 0 3px 3px',
                opacity: 0.6,
                animation: 'barBounceBottom 2.2s infinite ease-in-out',
                animationDelay: `${i * 0.15 + 0.5}s`
              }} />
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default function IncomeExpenditureDashboardTile({
  last12MonthsRentalPayments,
  last12MonthsRentalPaymentsLabels,
  loader,
  navigate,
  last12MonthsExpenditureReceipts,
  last12MonthsIncomeReceipts,
}) {
  const chartRef = useRef();
  const containerRef = useRef();
  const [tooltipData, setTooltipData] = useState(null);

  // Replaced useState and useEffect with useMemo to derive data synchronously
  const data = useMemo(() => {
    if (last12MonthsRentalPayments?.length > 0) {
      const incomeFromRentalPayments = last12MonthsRentalPaymentsLabels.map(
        (label) => {
          const labelRentalPayments = last12MonthsRentalPayments.filter(
            (rentalPayment) =>
              dayjs(rentalPayment?.rentalMonth).locale('de').format('MMM YY') ===
              label
          );
          return labelRentalPayments.reduce((sum, rentalPayment) => {
            const netRent =
              rentalPayment?.receivedNetRent?.parsedValue ??
              rentalPayment?.receivedNetRent ??
              0;
            const advPayment =
              rentalPayment?.receivedAdvancePayment?.parsedValue ??
              rentalPayment?.receivedAdvancePayment ??
              0;

            return sum + netRent + advPayment;
          }, 0);
        }
      );

      const allIncome = incomeFromRentalPayments.map(
        (income, index) =>
          income + (last12MonthsIncomeReceipts?.[index]?.totalCost || 0)
      );

      const expenditure = (last12MonthsExpenditureReceipts || []).map(
        (receipt) => receipt?.totalCost || 0
      );
      
      const balance = allIncome.map(
        (income, index) => income + (expenditure[index] || 0)
      );

      return {
        labels: [...last12MonthsRentalPaymentsLabels].reverse(),
        datasets: [
          {
            type: 'bar',
            label: 'Einnahmen',
            order: 2, 
            backgroundColor: colors.income,
            hoverBackgroundColor: colors.income,
            borderColor: colors.income,
            hoverBorderColor: colors.income,
            borderRadius: 4,
            borderWidth: 0,
            hoverOffset: 0,
            data: [...allIncome].reverse(),
          },
          {
            type: 'bar',
            label: 'Ausgaben',
            order: 2, 
            backgroundColor: colors.expenditure,
            hoverBackgroundColor: colors.expenditure,
            borderColor: colors.expenditure,
            hoverBorderColor: colors.expenditure,
            borderRadius: 4,
            borderWidth: 0,
            hoverOffset: 0,
            data: [...expenditure].reverse(),
          },
          {
            type: 'line',
            label: 'Saldo',
            order: 1, 
            backgroundColor: colors.balance,
            hoverBackgroundColor: colors.balance,
            borderColor: colors.balance,
            hoverBorderColor: colors.balance,
            borderRadius: 4,
            borderWidth: 1,
            hoverOffset: 0,
            fill: false,
            data: [...balance].reverse(),
          },
        ],
      };
    }
    return null;
  }, [
    last12MonthsRentalPayments, 
    last12MonthsRentalPaymentsLabels, 
    last12MonthsIncomeReceipts, 
    last12MonthsExpenditureReceipts
  ]);

  const isChartEmpty =
    data?.datasets?.every((dataset) =>
      dataset.data.every((val) => val === 0)
    ) ?? false;

  const options = {
    plugins: {
      legend: {
        position: 'right',
        align: 'start', 
        title: {
          display: true,
          text: ' ',
          padding: { top: 16 }, 
        },
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 12,
          boxHeight: 7,
          padding: 16,
          sort: (a, b) => {
            const order = ['Einnahmen', 'Ausgaben', 'Saldo'];
            return order.indexOf(a.text) - order.indexOf(b.text);
          },
        },
        onClick: (e, legendItem, legend) => {
          const chartLabels = legend.chart.data.labels;
          if (!chartLabels || chartLabels.length === 0) return;

          const firstLabel = chartLabels[0];
          const lastLabel = chartLabels[chartLabels.length - 1];

          const formattedDateStart = moment(firstLabel === 'Sept. 25' ? 'Sep. 25' : firstLabel, 'MMM YY').startOf('month').format('YYYY-MM-DDT00:00:00');
          const formattedDateEnd = moment(lastLabel === 'Sept. 25' ? 'Sep. 25' : lastLabel, 'MMM YY').endOf('month').format('YYYY-MM-DDT00:00:00');

          if (legendItem.text === 'Einnahmen') {
            navigate(`/user/rentIncomeTracking?startDate=${formattedDateStart}&endDate=${formattedDateEnd}`);
          } else if (legendItem.text === 'Ausgaben') {
            // Get the tax cost types and join them into a comma-separated string
            const taxCostTypes = getAllTaxCostTypes().join(',');
            navigate(`/accounting/receipts?startDate=${formattedDateStart}&endDate=${formattedDateEnd}&taxCostType=${encodeURIComponent(taxCostTypes)}`);
          } else {
            ChartJS.defaults.plugins.legend.onClick(e, legendItem, legend);
          }
        },
        onHover: (e) => {
          e.native.target.style.cursor = 'pointer';
        },
        onLeave: (e) => {
          e.native.target.style.cursor = 'default';
        }
      },
      tooltip: {
        enabled: false,
        external: (context) => externalTooltipHandler(context),
      },
    },
    hover: {
      mode: null,
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        border: {
          display: false, 
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
        },
      },
      y: {
        afterDataLimits: (scale) => {
          const maxAbs = Math.max(Math.abs(scale.max), Math.abs(scale.min));
          if (maxAbs > 0) {
            scale.max = maxAbs;
            scale.min = -maxAbs;
          }
        },
        grid: {
          display: true, 
          drawBorder: true, 
          color: (context) => (context.tick.value === 0 ? '#DADAE6' : 'transparent'),
          lineWidth: (context) => (context.tick.value === 0 ? 1 : 0),
        },
        border: {
          display: true, 
          color: '#DADAE6',
        },
        beginAtZero: true,
        ...(isChartEmpty && { suggestedMax: 1 }),
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6,
          ...(isChartEmpty && { stepSize: 1 }),
          callback: (value) => {
            if (isChartEmpty) {
              return value === 0 ? '0 €' : '';
            }
            return value.toLocaleString('de-DE') + ' €';
          },
        },
      },
    },
  };

  const renderTooltip = () => {
    return (
      <div
        style={{
          position: 'absolute',
          left: tooltipData?.x,
          top: tooltipData?.y + 75,
          backgroundColor: 'white',
          color: '#13135c',
          padding: '6px 10px',
          borderRadius: '4px',
          pointerEvents: 'none',
          transform: 'translate(-50%, -120%)',
          whiteSpace: 'nowrap',
          fontSize: 12,
          zIndex: 1000,
          boxShadow: '0 3px 3px rgba(0,0,0,.2)',
        }}
      >
        <div
          style={{
            color:
              tooltipData?.label === 'Saldo'
                ? colors.balance
                : tooltipData?.label === 'Einnahmen'
                ? colors.income
                : colors.expenditure,
          }}
        >
          <strong>{tooltipData?.label}</strong>
        </div>
        <div style={{ fontSize: '16px' }}>
          {accounting.formatMoney(
            tooltipData?.value,
            '€',
            2,
            '.',
            ',',
            '%v %s'
          )}
        </div>
        <div style={{ color: '#717182' }}>{tooltipData?.month}</div>
      </div>
    );
  };

  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;

    if (!tooltip || !tooltip.opacity || !tooltip.dataPoints) {
      setTooltipData(null);
      return;
    }

    const dataPoint = tooltip.dataPoints[0];

    const newTooltip = {
      label: dataPoint?.dataset?.label,
      month: dataPoint?.label,
      value: dataPoint?.raw,
      x: tooltip.caretX,
      y: tooltip.caretY,
    };

    setTooltipData((prev) => {
      if (
        !prev ||
        prev.label !== newTooltip.label ||
        prev.month !== newTooltip.month ||
        prev.value !== newTooltip.value ||
        prev.x !== newTooltip.x ||
        prev.y !== newTooltip.y
      ) {
        return newTooltip;
      }
      return prev;
    });
  };

  const handleSliceClick = (label, value, type) => {
    const formattedDateStart = moment(label === 'Sept. 25' ? 'Sep. 25' : label, 'MMM YY').startOf('month').format('YYYY-MM-DDT00:00:00');
    const formattedDateEnd = moment(label === 'Sept. 25' ? 'Sep. 25' : label, 'MMM YY').endOf('month').format('YYYY-MM-DDT00:00:00');

    if (type === 'Einnahmen') {
      navigate(`/user/rentIncomeTracking?startDate=${formattedDateStart}&endDate=${formattedDateEnd}`);
    }
    if (type === 'Ausgaben') {
      const taxCostTypes = getAllTaxCostTypes().join(',');
      navigate(`/accounting/receipts?startDate=${formattedDateStart}&endDate=${formattedDateEnd}&taxCostType=${encodeURIComponent(taxCostTypes)}`);
    }
  };

  return (
    <Stack className="rent-income-control-dashboard-card" id="rent-income-control-dashboard-card" spacing={5}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '180px' }}>
        {loader ? (
          <CustomChartLoader />
        ) : data ? (
          <Chart
            ref={chartRef}
            type="bar"
            data={data}
            options={{
              ...options,
              maintainAspectRatio: false,
            }}
            style={{ cursor: 'pointer' }}
            onClick={(event) => {
              if (!chartRef.current) return;
              const chart = chartRef.current;

              const elements = chart.getElementsAtEventForMode(
                event,
                'nearest',
                { intersect: true },
                true
              );

              if (!elements.length) return;

              const { datasetIndex, index } = elements[0];
              const label = chart.data.labels[index];
              const type = chart.data.datasets[datasetIndex].label;
              const value = chart.data.datasets[datasetIndex].data[index];

              handleSliceClick(label, value, type);
            }}
          />
        ) : (
          <EmptyStateDashboard
            heading={'Keine Daten verfügbar.'}
            subheading={'Es wurden noch keine Daten zu Einnahmen oder Ausgaben gefunden.'}
            className="emptystate-dashboard empty-state-btn-added"
            icon={RentIcon}
            buttonDisable={false}
            mainContainerStyling={{ width: '100%' }}
            imageWidth={'48'}
            imageHeight={'48'}
          />
        )}
      </div>
      {tooltipData && !loader && renderTooltip()}
    </Stack>
  );
}